import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types/episode";

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const goTo = (index: number) => setActive((index + slides.length) % slides.length);
  const slide = slides[active];

  return (
    <section className="relative w-full h-[560px] md:h-[680px] overflow-hidden bg-background-950">
      {slides.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.image}
            alt={`朋友的妈妈4 剧照 - ${item.title}`}
            title="朋友的妈妈4 在线观看与剧情介绍"
            className={`w-full h-full object-cover object-top ${
              index === active ? "anim-hero-zoom" : ""
            }`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/70 to-background-950/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-950/50" />

      <div className="relative z-10 h-full w-full max-w-[1280px] mx-auto px-4 md:px-8 flex items-end pb-14 md:pb-20">
        <div className="w-full max-w-2xl" key={slide.id}>
          <div className="flex items-center gap-3 anim-fade-up">
            <span className="px-2.5 h-6 rounded bg-primary-500 text-background-50 text-[11px] tracking-wider flex items-center">
              {slide.tag}
            </span>
            <span className="text-[11px] tracking-[0.2em] text-foreground-600 uppercase">
              {slide.eyebrow}
            </span>
          </div>
          <h2 className="mt-4 font-heading text-4xl md:text-6xl leading-tight text-foreground-950 anim-fade-up">
            {slide.title}
          </h2>
          <p className="mt-4 text-sm md:text-base leading-7 text-foreground-700 max-w-xl anim-fade-up">
            {slide.description}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3 anim-fade-up">
            <Link
              to={`/detail/${slide.episodeId}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-base" />
              观看本集
            </Link>
            <Link
              to="/episodes"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-foreground-700/40 hover:border-foreground-500 text-foreground-900 text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              全部 16 集
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 right-4 md:right-8 bottom-8 md:bottom-14 flex items-center gap-4">
        <div className="hidden sm:flex items-center gap-2">
          {slides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张剧照`}
              onClick={() => goTo(index)}
              className={`h-1 rounded-full transition-all cursor-pointer ${
                index === active ? "w-8 bg-accent-500" : "w-4 bg-foreground-700/50 hover:bg-foreground-600"
              }`}
            />
          ))}
        </div>
        <span className="font-label text-xs tracking-widest text-foreground-500">
          {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="上一张剧照"
            onClick={() => goTo(active - 1)}
            className="w-9 h-9 rounded-md border border-background-300/60 bg-background-950/60 backdrop-blur text-foreground-800 hover:border-primary-500 hover:text-foreground-950 transition-colors cursor-pointer flex items-center justify-center"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            type="button"
            aria-label="下一张剧照"
            onClick={() => goTo(active + 1)}
            className="w-9 h-9 rounded-md border border-background-300/60 bg-background-950/60 backdrop-blur text-foreground-800 hover:border-primary-500 hover:text-foreground-950 transition-colors cursor-pointer flex items-center justify-center"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </section>
  );
}